'use client'
import { AtNavbarLinkProps, OrFooter, OrFooterProps, OrNavbar, OrNavbarProps } from '@studio/atomic-ui'
import { MouseEvent, PropsWithChildren, useMemo } from 'react'
import { baseUrl } from '../env'
import { trackNavigationMenuClick } from '../utils/analytics'
import { AbTestingConfiguration, SelectedModuleVariants } from '../services/sanity/types'

export interface LayoutProps {
  navbar?: OrNavbarProps
  abTestingConfiguration?: AbTestingConfiguration
  moduleABTestingConfiguration?: SelectedModuleVariants
  footer?: OrFooterProps
  title: string
  pageType?: string
  id?: string
}

export const Layout = ({ children, footer, navbar, title }: PropsWithChildren<LayoutProps>) => {
  const memoizedNavbar = useMemo(() => {
    if (!navbar) return navbar

    const componentName =
      !navbar.buttons && !navbar.links ? 'navbar simple' : title === 'Checkout' ? 'navbar checkout' : 'navbar full'
    const originURL = baseUrl ? new URL(baseUrl).origin : ''

    const buttons = navbar.buttons?.map((button) => {
      return {
        ...button,
        onTrackClick: (event?: MouseEvent<HTMLButtonElement>) => {
          event?.stopPropagation()
          trackNavigationMenuClick({
            menuTitle: button.title?.toLocaleLowerCase() || '',
            menuHeading: button.title?.toLocaleLowerCase() || '',
            menuType: 'main menu navigation',
            linkUrl: `${baseUrl}${button.openUrl}` || '',
            componentName,
            windowLocationHref: window.location.href,
          })
        },
      }
    })

    const links = navbar.links
      ?.filter((l) => !!l)
      .map((link) => {
        const onTrackClick = (navbarLink: AtNavbarLinkProps) => {
          return (event?: MouseEvent<HTMLAnchorElement>) => {
            event?.stopPropagation()
            trackNavigationMenuClick({
              menuTitle: navbarLink.label?.toLocaleLowerCase() || '',
              menuHeading: navbarLink.label?.toLocaleLowerCase() || '',
              menuType: 'main menu navigation',
              linkUrl: navbarLink.href || '',
              componentName,
              windowLocationHref: window.location.href,
            })
          }
        }

        if (link.type === 'dropdown') {
          return {
            ...link,
            dropdownLinks: link.dropdownLinks.map((l) => ({
              ...l,
              onTrackClick: onTrackClick(l),
            })),
          }
        }

        return {
          ...link,
          onTrackClick: onTrackClick(link),
        }
      })

    const onLogoClick = (event?: MouseEvent<HTMLAnchorElement>) => {
      event?.stopPropagation()
      trackNavigationMenuClick({
        menuTitle: 'home logo',
        menuHeading: 'home logo',
        menuType: 'main menu navigation',
        linkUrl: originURL,
        componentName,
        windowLocationHref: window.location.href,
      })
    }
    const onBackClick = (event?: MouseEvent<HTMLAnchorElement>) => {
      event?.stopPropagation()
      trackNavigationMenuClick({
        menuTitle: 'back to space details',
        menuHeading: 'back to space details',
        menuType: 'main menu navigation',
        linkUrl: navbar.checkoutHref || '',
        componentName,
        windowLocationHref: window.location.href,
      })
    }

    return { ...navbar, buttons, links, onLogoClick, onBackClick }
  }, [navbar, title])

  const memoizedFooter = useMemo(() => {
    if (!footer) return footer

    const links = footer.links?.map((link) => {
      return {
        ...link,
        onClick: (event?: MouseEvent<HTMLAnchorElement>) => {
          event?.stopPropagation()
          trackNavigationMenuClick({
            menuTitle: link.title?.toLocaleLowerCase() || '',
            menuHeading: link.title?.toLocaleLowerCase() || '',
            menuType: 'footer menu navigation',
            linkUrl: link.actionTarget || '',
            componentName: 'footer',
            windowLocationHref: window.location.href,
          })
        },
      }
    })

    const secondaryLinks = footer.secondaryLinks?.map((link) => {
      return {
        ...link,
        onClick: (event?: MouseEvent<HTMLAnchorElement>) => {
          event?.stopPropagation()
          trackNavigationMenuClick({
            menuTitle: link.title?.toLocaleLowerCase() || '',
            menuHeading: link.title?.toLocaleLowerCase() || '',
            menuType: 'footer menu navigation',
            linkUrl: link.actionTarget || '',
            componentName: 'footer',
            windowLocationHref: window.location.href,
          })
        },
      }
    })

    return { ...footer, links, secondaryLinks }
  }, [footer])

  return (
    <>
      {memoizedNavbar && <OrNavbar {...memoizedNavbar} />}
      <main id="main-content" className="min-h-screen">
        {children}
      </main>
      {memoizedFooter && <OrFooter {...memoizedFooter} />}
    </>
  )
}
